import { useUserManagementServiceV1 } from "@rw1/api-services";
import { useQuery } from "@tanstack/react-query";
import { UserGroupAccessData } from "packages/auto-generated-clients/src/user-management-client-v1.generated";
import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { useExternalExperienceGroups } from "./useExternalExperienceGroups";

export const USER_GROUPS_FOR_RELEASE_CHANNEL_QUERY_KEY =
  "userGroupsForReleaseChannel";

interface UseUserGroupsForReleaseChannelResult {
  groupAccess: Array<{
    userGroupId: string;
    expiresAt?: Date | null;
  }>;
  isLoading: boolean;
}

interface UseUserGroupsForReleaseChannelOptions {
  enabled?: boolean;
  isExternal?: boolean;
}

export const useUserGroupsForReleaseChannel = (
  releaseChannelId: string | undefined,
  options: UseUserGroupsForReleaseChannelOptions = {},
): UseUserGroupsForReleaseChannelResult => {
  const { enabled = true, isExternal = false } = options;
  const userManagementClient = useUserManagementServiceV1();
  const { companyId } = useParams();

  const { rows: externalRows, loading: loadingExternal } =
    useExternalExperienceGroups({
      releaseChannelId,
      companyId,
      enabled: isExternal && enabled,
    });

  const { data, isLoading } = useQuery({
    queryKey: [USER_GROUPS_FOR_RELEASE_CHANNEL_QUERY_KEY, releaseChannelId],
    staleTime: 1000 * 60 * 5,
    enabled: !isExternal && !!releaseChannelId && enabled,
    queryFn: async (): Promise<UserGroupAccessData[]> => {
      if (!releaseChannelId) return [];

      const allResults: UserGroupAccessData[] = [];
      let pageNumber = 0;
      const pageSize = 1000;
      let hasMore = true;

      while (hasMore) {
        const results =
          await userManagementClient.getUserGroupsForReleaseChannel(
            releaseChannelId,
            pageNumber,
            pageSize,
          );

        if (Array.isArray(results)) {
          allResults.push(...results);

          if (results.length < pageSize) {
            hasMore = false;
          } else {
            pageNumber++;
          }
        } else {
          hasMore = false;
        }
      }

      return allResults;
    },
  });

  const externalGroupAccess = useMemo(() => {
    return externalRows.map((row) => ({
      userGroupId: row.userGroupId,
      expiresAt: row.expiresAt,
    }));
  }, [externalRows]);

  const internalGroupAccess = useMemo(() => {
    if (!data) return [];

    return data.map((item) => ({
      userGroupId: item.userGroupId,
      expiresAt: item.expiresAt,
    }));
  }, [data]);

  return {
    groupAccess: isExternal ? externalGroupAccess : internalGroupAccess,
    isLoading: isExternal ? loadingExternal : isLoading,
  };
};
